import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Stack } from "@fluentui/react";
import { builtinFormatters, useTheme } from "cayo.ui";
import DOMPurify from "dompurify";
import { IAlertItem } from "@cayo/shared/components/Alerts/types";
import { isDate } from "@cayo/shared/types/typeGuards";
import {
  AlertFooter,
  AlertItemContainer,
  AlertItemDescription,
  DateText,
  DetailsText,
  DismissIconButton,
  SeverityIcon,
  SmallLink,
} from "./styles";

type OnDismissFunc = (item: IAlertItem) => Promise<void>;

const AlertItem: FC<
  IAlertItem & {
    onDismiss: OnDismissFunc | null;
    showDetails: () => void;
    index: number;
  }
> = (props) => {
  const intl = useIntl();
  const { getColor, getIcon } = useTheme();

  const {
    lastModifiedDateTime,
    subject,
    message,
    highlighted,
    index,
    severity,
    showDetails,
    onDismiss,
    disabled,
  } = props;

  const alertDateTime = builtinFormatters.dateTime(
    isDate(lastModifiedDateTime) ? lastModifiedDateTime.toISOString() : lastModifiedDateTime,
    intl
  );
  const isRisk = props?.objectType === "cayo.graph.riskAlert";

  const [isHovered, setIsHovered] = useState(false);
  const [disappeared, setIsDisappeared] = useState<boolean>(false);

  return (
    <AlertItemContainer
      disappeared={disappeared}
      onMouseOver={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => !disabled && setIsHovered(false)}
      highlighted={highlighted}
      isFirst={index === 0}
      data-testid="alert-item"
    >
      <SeverityIcon
        styles={{ root: { color: getColor(severity, "severity") } }}
        iconName={getIcon(severity, "severity")}
      />
      {!isRisk && onDismiss && (
        <DismissIconButton
          styles={{ icon: { fontSize: 10 } }}
          iconProps={{ iconName: "Clear" }}
          onClick={(e) => {
            e.stopPropagation();
            setIsDisappeared(true);
            setTimeout(() => {
              onDismiss!(props);
            }, 500);
          }}
          isHovered={isHovered}
          ariaLabel="Dismiss alert"
        />
      )}

      <Stack
        onClick={showDetails}
        tokens={{ childrenGap: 20 }}
        styles={{ root: { width: "100%" } }}
      >
        <Stack tokens={{ childrenGap: 2 }}>
          <DetailsText
            $linesNumber={3}
            isHovered={isHovered}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(subject || ""),
            }}
          />
          {message && (
            <AlertItemDescription
              $linesNumber={3}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
            />
          )}
        </Stack>
        <AlertFooter>
          <SmallLink
            disabled={disabled}
            onClick={(e: any) => {
              e.stopPropagation();
              showDetails();
            }}
          >
            <FormattedMessage
              id="alert-item.click-here-for-detials"
              defaultMessage="Click for details"
            />
          </SmallLink>
          {alertDateTime && (
            <DateText variant="small" block nowrap>
              {alertDateTime}
            </DateText>
          )}
        </AlertFooter>
      </Stack>
    </AlertItemContainer>
  );
};

export default AlertItem;
